import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { windowText } from "../components/Countdown";
import { api, money, formatApiErrorDetail, customText } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { AcademySelect } from "../components/AcademySelect";
import { startStripeCheckout } from "./PaymentSuccess";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShippingOptions } from "../components/ShippingOptions";

export default function Checkout() {
  const { items, subtotal, academy, setAcademy } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    line1: "", city: "", state: "", zip: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [taxCfg, setTaxCfg] = useState({ rate: 0, states: [] });
  useEffect(() => {
    api.get("/settings/tax").then(({ data }) => setTaxCfg(data)).catch(() => {});
  }, []);
  const [shipOpt, setShipOpt] = useState(null);
  const shipping = shipOpt?.price ?? 0;
  const st = form.state.trim().toUpperCase();
  const taxable = taxCfg.states.includes(st) || (st.startsWith("CALIF") && taxCfg.states.includes("CA"));
  const tax = taxable ? Math.round(subtotal * taxCfg.rate) / 100 : 0;
  const total = subtotal + shipping + tax;

  useEffect(() => {
    if (items.length === 0) navigate("/shop");
  }, [items.length, navigate]);

  if (items.length === 0) return null;

  const groupItems = () => {
    const grouped = {};
    items.forEach((it) => {
      const key = `${it.product_id}|${it.colorway || it.color || ""}|${it.audience || ""}|${JSON.stringify(it.customization || {})}`;
      grouped[key] = grouped[key] || { product_id: it.product_id, color: it.color, colorway: it.colorway || null, audience: it.audience || null, customization: it.customization || null, sizes: [] };
      grouped[key].sizes.push({ size: it.size, qty: it.qty });
    });
    return Object.values(grouped);
  };
  const orderItems = groupItems();

  const placeOrder = async () => {
    if (!form.customer_name || !form.email) return toast.error("Name and email are required");
    if (!form.line1 || !form.city || !form.state || !form.zip) return toast.error("Please enter your full shipping address");
    if (!academy) return toast.error("Please select your academy");
    if (!shipOpt) return toast.error("Please choose a shipping option");
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        customer_name: form.customer_name,
        email: form.email,
        phone: form.phone,
        academy_id: academy.id,
        academy_other: academy.id ? null : academy.name,
        shipping_address: { line1: form.line1, city: form.city, state: form.state, zip: form.zip },
        billing_address: { line1: form.line1, city: form.city, state: form.state, zip: form.zip },
        items: orderItems,
        save_academy: !!user,
        shipping_service: shipOpt.service,
      });
      await startStripeCheckout(data, "retail");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-8 py-10">
      <h1 className="font-heading font-black uppercase text-4xl text-white mb-8">Checkout</h1>
      <p data-testid="checkout-preorder-note" className="font-mono text-xs text-yellow-400/80 -mt-6 mb-6 uppercase tracking-widest">Preorder · Winter '26 · {windowText()}</p>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-[#121216] border border-white/10 rounded-lg p-6">
            <h3 className="font-heading font-bold uppercase text-white text-xl mb-4">Contact</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-zinc-400">Full Name</Label><Input data-testid="checkout-name" value={form.customer_name} onChange={set("customer_name")} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
              <div><Label className="text-zinc-400">Email</Label><Input data-testid="checkout-email" value={form.email} onChange={set("email")} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
              <div><Label className="text-zinc-400">Phone</Label><Input data-testid="checkout-phone" value={form.phone} onChange={set("phone")} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
            </div>
          </section>

          <section className="bg-[#121216] border border-white/10 rounded-lg p-6">
            <h3 className="font-heading font-bold uppercase text-white text-xl mb-4">Academy Association</h3>
            <AcademySelect value={academy} onChange={setAcademy} testId="checkout-academy" />
          </section>

          <section className="bg-[#121216] border border-white/10 rounded-lg p-6">
            <h3 className="font-heading font-bold uppercase text-white text-xl mb-4">Shipping Address</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label className="text-zinc-400">Address</Label><Input data-testid="checkout-line1" value={form.line1} onChange={set("line1")} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
              <div><Label className="text-zinc-400">City</Label><Input data-testid="checkout-city" value={form.city} onChange={set("city")} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
              <div><Label className="text-zinc-400">State</Label><Input data-testid="checkout-state" value={form.state} onChange={set("state")} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
              <div><Label className="text-zinc-400">ZIP</Label><Input data-testid="checkout-zip" value={form.zip} onChange={set("zip")} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
            </div>
          </section>

          <section className="bg-[#121216] border border-white/10 rounded-lg p-6">
            <h3 className="font-heading font-bold uppercase text-white text-xl mb-4">Shipping Method</h3>
            <ShippingOptions items={orderItems} zip={form.zip} value={shipOpt} onChange={setShipOpt} />
          </section>

          <section className="bg-[#121216] border border-white/10 rounded-lg p-6">
            <h3 className="font-heading font-bold uppercase text-white text-xl mb-2">Payment</h3>
            <p data-testid="checkout-payment-note" className="text-zinc-400 text-sm">You'll be taken to Square's secure payment page to pay by card, Apple Pay or Google Pay. Your preorder is confirmed as soon as payment goes through.</p>
          </section>
        </div>

        <div className="bg-[#121216] border border-white/10 rounded-lg p-6 h-fit">
          <h3 className="font-heading font-bold uppercase text-white text-xl mb-4">Summary</h3>
          <div className="space-y-2 max-h-52 overflow-auto mb-4">
            {items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm text-zinc-400">
                <span className="pr-2">{it.qty}× {it.name}<span className="block text-xs text-zinc-500">{it.color ? `${it.color} · ` : ""}Size {it.size} · {money(it.unit_price)} ea{customText(it.customization) && <span className="block text-yellow-400">{customText(it.customization)}</span>}</span></span>
                <span className="font-mono">{money(it.unit_price * it.qty)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-3 space-y-2">
            <div className="flex justify-between text-zinc-300"><span>Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
            <div className="flex justify-between gap-3 text-zinc-300"><span>Shipping{shipOpt ? ` (${shipOpt.label})` : ""}</span><span data-testid="checkout-shipping" className="font-mono">{shipOpt ? money(shipping) : "Enter ZIP"}</span></div>
            <div className="flex justify-between text-zinc-300"><span>Sales tax{taxable ? ` (${taxCfg.rate}%)` : ""}</span><span data-testid="checkout-tax" className="font-mono">{taxable ? money(tax) : st ? money(0) : "Enter state"}</span></div>
            <div className="flex justify-between text-white font-bold text-lg pt-2"><span>Total</span><span className="font-mono text-yellow-400">{money(total)}</span></div>
          </div>
          <button data-testid="place-order-btn" disabled={submitting || !shipOpt} onClick={placeOrder} className="btn-primary w-full px-6 py-3.5 rounded-sm mt-5 disabled:opacity-50">
            {submitting ? "Redirecting to secure payment…" : `Pay ${money(total)} with Card`}
          </button>
        </div>
      </div>
    </div>
  );
}
